import React, { useState, JSX } from 'react';
import { observer } from 'mobx-react';
import { ApiCancelRequest } from '@ibe/api';
import { Modal, ModalBody } from 'reactstrap';
import useAppService from '../../Hooks/useAppService';
import Info from '../UI/Messages/Info';
import Error from '../UI/Messages/Error';
import fallback from '../../../_Translations/generated/cancel-request.en.t.json';
import Keys from '../../../_Translations/generated/cancel-request.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';
import OverlayContainer from '../UI/LoadingOverlay/OverlayContainer';
import Overlay from '../UI/LoadingOverlay/Overlay';
import CancelRequest from './CancelRequest';
import useWindow from '../../Hooks/useWindow';

export interface Props {
  isOpen: boolean;
  setIsOpen: (newValue: boolean) => void;
  bookingNumber: string;
  withNoAuthorization?: boolean;
}

const CancelRequestModule = observer(
  (props: Props): JSX.Element => {
    const window = useWindow();
    const { isOpen, setIsOpen, bookingNumber } = props;
    const { t } = useTranslation('cancel-request', fallback, Keys);

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);

    const apiService = useAppService();

    function onSendAction(cancelRequest: ApiCancelRequest): void {
      setLoading(true);
      setError(false);
      setSuccess(false);

      apiService.api
        .sendCancelRequest(cancelRequest)
        .then(() => {
          setSuccess(true);
        })
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    }

    function onCancelAction(): void {
      if (setIsOpen) {
        setIsOpen(false);
      }
    }

    const Content = (): JSX.Element => {
      return (
        <div>
          <OverlayContainer>
            <div>
              <CancelRequest
                onSendAction={onSendAction}
                onCancelAction={onCancelAction}
                bookingNumber={bookingNumber}
              />
              <div className="pt-2">
                {success && <Info message={t(Keys.messages.success)} />}
                {error && <Error message={t(Keys.messages.error)} />}
              </div>
            </div>
            {loading && <Overlay />}
          </OverlayContainer>
        </div>
      );
    };

    return (
      <div key="cancel_request_module">
        <Modal
          isOpen={isOpen}
          toggle={(): void => {
            setIsOpen(!isOpen);
          }}
          backdrop="static"
          keyboard={false}
          className="iso__modal-dialog"
          size="lg"
          container={window?.document?.getElementById('iso') || undefined}
        >
          <ModalBody>
            <Content />
          </ModalBody>
        </Modal>
      </div>
    );
  }
);

export default CancelRequestModule;
